/*!
 * PP Cart Samples v2 — cart drawer sample picker handler
 * Wires [data-sample-btn] tiles in snippets/cart-free-samples.liquid to
 * /cart/add.js and /cart/change.js. Max 2 samples. Relies on Pipeline's
 * theme:cart:reload event to refresh drawer HTML (server-rendered is-selected
 * state is the source of truth). Pricing lives on the two automatic BxGy
 * discounts ("Free Sample with Purchase" ×1 and "2 Free Samples with
 * Purchase" ×2, combinable), so 1 OR 2 picked samples are both free.
 *
 * v2 (2026-07-02):
 * - remove targets the LINE KEY: /cart/change.js rejects a numeric variant id
 *   with 400 ("expected Integer to be \d+ or \d+:hash"), so unselect never
 *   worked — resolve the line via /cart.js first.
 * - picker-added lines carry a hidden "_pp_sample" property so they can be
 *   told apart from a deliberately purchased 2ml.
 * - auto-heal: when no qualifying full-size remains in the cart, picker-added
 *   samples are removed instead of silently turning into paid €15 lines; a
 *   picker line bumped above qty 1 is clamped back.
 * - theme:cart:change is dispatched WITH {detail:{cart}} — Pipeline's
 *   listeners read e.detail.cart and threw on the bare event v1 sent.
 */
(function () {
  'use strict';

  var MAX_SAMPLES = 2;
  var BTN_SEL = '[data-sample-btn]';
  var CONTAINER_SEL = '[data-cart-samples]';
  var DS_HANDLE = 'premiere-peau-discovery-set';
  var SAMPLE_PROP = '_pp_sample';
  var inFlight = false;
  var healing = false;

  function jsonHeaders() {
    return { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed');
      return r.json();
    });
  }

  function changeByKey(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ id: String(key), quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('change failed');
      return r.json();
    });
  }

  function addSample(variantId) {
    var props = {};
    props[SAMPLE_PROP] = '1';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props }] })
    }).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return { description: 'add failed' }; })
          .then(function (e) { throw new Error(e.description || 'add failed'); });
      }
      return r.json();
    });
  }

  function removeSample(variantId) {
    var vid = String(variantId);
    return getCart().then(function (cart) {
      var line = null;
      var i;
      // prefer the picker-added line; fall back to any line of that variant
      for (i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (String(it.variant_id) !== vid) continue;
        if (it.properties && it.properties[SAMPLE_PROP]) { line = it; break; }
        if (!line) line = it;
      }
      if (!line) return cart;
      return changeByKey(line.key, 0);
    });
  }

  function reloadDrawer() {
    document.dispatchEvent(new CustomEvent('theme:cart:reload'));
    getCart().then(function (cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
    }).catch(function () { /* count refresh is cosmetic; drawer already reloading */ });
  }

  function isSampleLine(item) {
    return !!(item.properties && item.properties[SAMPLE_PROP]);
  }

  function isQualifyingLine(item) {
    if (isSampleLine(item)) return false;
    if (item.handle === DS_HANDLE) return false;
    var vt = (item.variant_title || '').toLowerCase();
    var vsku = (item.sku || '').toUpperCase();
    if (vt.indexOf('2ml') !== -1 || vsku.indexOf('SP2ML') !== -1) return false;
    return true;
  }

  /* Free samples only exist alongside a full-size purchase. If the bottle
     leaves the cart, drop the picker-added samples (the BxGy discount stops
     applying and they'd become paid lines); clamp picker lines back to qty 1. */
  function heal(cart) {
    if (healing || inFlight) return;
    var samples = cart.items.filter(isSampleLine);
    if (!samples.length) return;
    var qualifying = cart.items.some(isQualifyingLine);
    var ops = [];
    if (!qualifying) {
      ops = samples.map(function (s) { return { key: s.key, quantity: 0 }; });
    } else {
      ops = samples.filter(function (s) { return s.quantity > 1; })
        .map(function (s) { return { key: s.key, quantity: 1 }; });
    }
    if (!ops.length) return;
    healing = true;
    ops.reduce(function (p, op) {
      return p.then(function () { return changeByKey(op.key, op.quantity); });
    }, Promise.resolve())
      .then(function () { reloadDrawer(); })
      .catch(function (err) { console.error('[cart-samples] heal', err); })
      .finally(function () { healing = false; });
  }

  function handleClick(btn) {
    if (inFlight) return;
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var container = btn.closest(CONTAINER_SEL);
    var selectedCount = container
      ? container.querySelectorAll(BTN_SEL + '.is-selected').length
      : 0;
    var isSelected = btn.classList.contains('is-selected');

    if (!isSelected && selectedCount >= MAX_SAMPLES) {
      btn.classList.add('is-disabled');
      setTimeout(function () { btn.classList.remove('is-disabled'); }, 500);
      return;
    }

    inFlight = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    var action = isSelected ? removeSample(variantId) : addSample(variantId);
    action
      .then(function () { reloadDrawer(); })
      .catch(function (err) {
        console.error('[cart-samples]', err);
        btn.classList.add('is-error');
        setTimeout(function () { btn.classList.remove('is-error'); }, 800);
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        inFlight = false;
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest(BTN_SEL);
    if (!btn) return;
    e.preventDefault();
    handleClick(btn);
  });

  document.addEventListener('theme:cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && cart.items) heal(cart);
  });

  // Initial pass for carts landing on any page (incl. /cart) with a dead bottle.
  // Skip the fetch when the header count already says the cart is empty.
  (function initialHeal() {
    var run = function () {
      var el = document.querySelector('[data-header-cart-count]');
      if (el && (el.textContent || '').replace(/\D/g, '') === '0') return;
      getCart().then(heal).catch(function () { /* offline/etc — nothing to heal */ });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  })();
})();
